import { z } from 'zod';
import { IntegrationHealth } from '../enums.js';
import { IsoDateTime, UuidString } from '../primitives.js';
import { ConnectorCapability, SchedulePreset } from './connector.js';

/**
 * Integrations platform — Connection (экземпляр интеграции у тенанта) — B4.
 *
 * Обобщает legacy `integration_configs`: provider_key вместо `peer`-enum,
 * holding-level (holding_id) ИЛИ entity-level (entity_id), scope/schedule/
 * routing/mapping. Self-service: тенант создаёт из каталога без разработчика.
 */

/** Status жизненного цикла connection. */
export const ConnectionStatus = z.enum(['active', 'disabled', 'error']);
export type ConnectionStatus = z.infer<typeof ConnectionStatus>;

/** scope_json — какие ресурсы тянем + provider-фильтры (B12 enforcement). */
export const ConnectionScope = z.object({
  resources: z.array(ConnectorCapability).default([]),
  // Adesk-специфичные фильтры (provider-driven, опц.).
  accounts: z.array(z.string()).optional(),
  date_from: z.string().date().optional(),
  categories: z.array(z.string()).optional(),
});
export type ConnectionScope = z.infer<typeof ConnectionScope>;

/** routing_map — external_org_id → staiter_entity_id (multi_entity, B6). */
export const RoutingMap = z.record(z.string(), UuidString.nullable());
export type RoutingMap = z.infer<typeof RoutingMap>;

/** mapping_json — внешняя статья/группа → наш ДДС target (passthrough default, B15/F6). */
export const MappingJson = z.record(z.string(), z.string());
export type MappingJson = z.infer<typeof MappingJson>;

export const Connection = z.object({
  id: UuidString,
  provider_key: z.string().min(1).max(40),
  holding_id: UuidString.nullable(),
  entity_id: UuidString.nullable(),
  status: ConnectionStatus,
  health: IntegrationHealth,
  schedule: SchedulePreset,
  schedule_cron: z.string().nullable(),
  scope: ConnectionScope,
  routing_map: RoutingMap,
  mapping_json: MappingJson,
  last_pull_at: IsoDateTime.nullable(),
  last_attempt_at: IsoDateTime.nullable(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type Connection = z.infer<typeof Connection>;

export const ConnectionListResponse = z.object({
  items: z.array(Connection),
});
export type ConnectionListResponse = z.infer<typeof ConnectionListResponse>;

/** B4 — POST /connections (create из каталога). holding XOR entity. */
export const ConnectionCreateInput = z
  .object({
    provider_key: z.string().min(1).max(40),
    holding_id: UuidString.nullable().optional(),
    entity_id: UuidString.nullable().optional(),
    // Creds plaintext — шифруются на BE (B3 vault), маскируются на read.
    credentials: z.record(z.string()),
    scope: ConnectionScope.partial().optional(),
    schedule: SchedulePreset.optional(),
    schedule_cron: z.string().nullable().optional(),
    routing_map: RoutingMap.optional(),
    mapping_json: MappingJson.optional(),
  })
  // zod-strict-ok: input request-body
  .strict();
export type ConnectionCreateInput = z.infer<typeof ConnectionCreateInput>;

/** B4 — PUT /connections/:id (update; creds optional — ротация). */
export const ConnectionUpdateInput = z
  .object({
    status: ConnectionStatus.optional(),
    credentials: z.record(z.string()).optional(),
    scope: ConnectionScope.partial().optional(),
    schedule: SchedulePreset.optional(),
    schedule_cron: z.string().nullable().optional(),
    routing_map: RoutingMap.optional(),
    mapping_json: MappingJson.optional(),
  })
  // zod-strict-ok: input request-body
  .strict();
export type ConnectionUpdateInput = z.infer<typeof ConnectionUpdateInput>;

// ─── B5 — testConnection sample (capability-aware) ────────────────────────

/** Внешнее юрлицо из listExternalEntities (B6) — для routing UI (F3). */
export const ExternalEntity = z.object({
  external_id: z.string(),
  name: z.string(),
});
export type ExternalEntity = z.infer<typeof ExternalEntity>;

export const TestConnectionResult = z.object({
  health: IntegrationHealth,
  // capability-aware counts — присутствуют только если provider их умеет.
  counterparties_total: z.number().int().nullable().optional(),
  operations_total: z.number().int().nullable().optional(),
  external_entities: z.array(ExternalEntity).nullable().optional(),
  // degraded → reason-code (FE локализует через m.error_by_code).
  reason_code: z.string().nullable().optional(),
});
export type TestConnectionResult = z.infer<typeof TestConnectionResult>;

// ─── B10 — sync summary ───────────────────────────────────────────────────

export const SyncResourceCounts = z.object({
  imported: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
  unmatched: z.number().int(),
  errors: z.number().int(),
});
export type SyncResourceCounts = z.infer<typeof SyncResourceCounts>;

export const SyncSummary = z.object({
  connection_id: UuidString,
  by_resource: z.record(z.string(), SyncResourceCounts),
  by_entity: z.record(z.string(), SyncResourceCounts),
  started_at: IsoDateTime,
  finished_at: IsoDateTime.nullable(),
});
export type SyncSummary = z.infer<typeof SyncSummary>;

/** POST /connections/:id/sync — enqueue ответ. */
export const SyncTriggerResponse = z.object({
  job_id: z.string(),
  connection_id: UuidString,
});
export type SyncTriggerResponse = z.infer<typeof SyncTriggerResponse>;

/** B17 — reconcile_status для payments (test-only seed vs live). */
export const ReconcileStatus = z.enum(['live', 'superseded', 'seed_only', 'ambiguous']);
export type ReconcileStatus = z.infer<typeof ReconcileStatus>;
